import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PlayTable from "../PlayTable/PlayTable";
import SolutionPlayer, { simulateLowestLine } from "./SolutionPlayer";
import SolutionEditor from "./SolutionEditor";
import { loadSolution } from "./solutionStore";
import { markPlayed, PH_CATEGORY } from "./problemProgress";
import { savePracticeCompletion } from "../../store/actions/usersActions";
import { PROBLEM_HANDS } from "./problemHandsData";
import { GAME_PLAN_PROBLEM_IDS } from "../MysteryHand/mysteryHandData";
import { recordMysteryResult } from "../MysteryHand/mysteryResults";
import { suitOrderForTrump } from "../PlayTable/bridgeCore";
import "./ProblemHands.css";

const STRAIN_SYM = { S: "♠", H: "♥", D: "♦", C: "♣", N: "NT" };
const STRAIN_CLASS = { S: "ph-black", H: "ph-red", D: "ph-red", C: "ph-club", N: "" };
const SUIT_SYM = { S: "♠", H: "♥", D: "♦", C: "♣" };
const SUIT_CLASS = { S: "ph-black", H: "ph-red", D: "ph-red", C: "ph-club" };

const RANK_ORDER = "AKQJT98765432";
const SUIT_DISPLAY_ORDER = ["S", "H", "C", "D"];

// Colour any raw suit symbols (♠♥♦♣) inside a string of prose (e.g. intro text
// that mentions a contract like "5♥"). ♥/♦ red, ♠ black, ♣ green.
const SUIT_SYM_CLASS = { "♠": "ph-black", "♥": "ph-red", "♦": "ph-diamond", "♣": "ph-club" };
function withColoredSuits(text) {
  return String(text)
    .split(/([♠♥♦♣])/)
    .map((part, i) =>
      SUIT_SYM_CLASS[part] ? (
        <span key={i} className={SUIT_SYM_CLASS[part]}>{part}</span>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
}

function HandBlock({ seat, hand, suits }) {
  const order = suits || SUIT_DISPLAY_ORDER;
  const bySuit = {};
  for (const c of hand) {
    if (!bySuit[c.suit]) bySuit[c.suit] = [];
    bySuit[c.suit].push(c);
  }
  return (
    <div className="ph-handBlock">
      <div className="ph-handSeat">{seat}</div>
      {order.map((s) => {
        const cards = (bySuit[s] || []).slice().sort(
          (a, b) => RANK_ORDER.indexOf(a.rank) - RANK_ORDER.indexOf(b.rank)
        );
        return (
          <div key={s} className="ph-handSuitRow">
            <span className={`ph-suitSym ${SUIT_CLASS[s]}`}>{SUIT_SYM[s]}</span>
            <span className="ph-handRanks">
              {cards.length ? cards.map((c) => (c.rank === "T" ? "10" : c.rank)).join("") : "—"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function HandDiagram({ hands, suits }) {
  return (
    <div className="ph-diagram">
      <div className="ph-diagRow ph-diagRow--top">
        <HandBlock seat="N" hand={hands.N} suits={suits} />
      </div>
      <div className="ph-diagRow ph-diagRow--mid">
        <HandBlock seat="W" hand={hands.W} suits={suits} />
        <div className="ph-diagVoid" />
        <HandBlock seat="E" hand={hands.E} suits={suits} />
      </div>
      <div className="ph-diagRow ph-diagRow--bot">
        <HandBlock seat="S" hand={hands.S} suits={suits} />
      </div>
    </div>
  );
}

function callLabel(call) {
  if (!call) return null;
  if (call.kind === "pass") return <span className="ph-pass">P</span>;
  if (call.kind === "double") return <span className="ph-dbl">X</span>;
  if (call.kind === "redouble") return <span className="ph-rdbl">XX</span>;
  return (
    <span>
      {call.level}
      <span className={STRAIN_CLASS[call.strain]}>{STRAIN_SYM[call.strain]}</span>
    </span>
  );
}

function AuctionGrid({ auction, dealer }) {
  const COLS = ["W", "N", "E", "S"];
  const dealerIdx = COLS.indexOf(dealer);
  const rows = [];
  let row = [];
  for (let i = 0; i < dealerIdx; i++) row.push(null);
  for (const entry of auction) {
    row.push(entry);
    if (row.length === 4) {
      rows.push(row);
      row = [];
    }
  }
  if (row.length) {
    while (row.length < 4) row.push(null);
    rows.push(row);
  }
  return (
    <table className="ph-auction">
      <thead>
        <tr>
          {COLS.map((c) => <th key={c}>{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((entry, j) => (
              <td key={j}>{entry ? callLabel(entry.call) : ""}{entry && entry.alert ? <span className="ph-alert">*</span> : null}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProblemHandPlay({ problem, uid, displayName, subscriptionActive, tier, isAdmin, authReady, savePracticeCompletion }) {
  // ?solution=1 opens straight on the solution (the Game Plan panel links here after a hand).
  const [phase, setPhase] = useState(() =>
    typeof window !== "undefined" && /[?&]solution=1/.test(window.location.search) ? "solution" : "playing"
  );

  const handleExit = useCallback(() => setPhase("solution"), []);
  const handlePlayAgain = useCallback(() => setPhase("playing"), []);
  // Mark this hand "played" the moment it finishes at the table (done or passed
  // out), independent of whether the member then opens the solution.
  // Local tick straight away, and into the account (membersData completedPractice.problemHands) when signed in.
  const recordPlayed = useCallback(() => {
    markPlayed(problem.id);
    if (uid) savePracticeCompletion(uid, PH_CATEGORY, problem.id);
  }, [problem.id, uid, savePracticeCompletion]);
  // A problem hand that is also on the Game Plan page scores there too, wherever it is played.
  const handleHandResult = useCallback((record) => {
    recordPlayed();
    if (uid && record && GAME_PLAN_PROBLEM_IDS.includes(problem.id)) {
      recordMysteryResult(problem.id, uid, displayName, record).catch(() => {});
    }
  }, [recordPlayed, uid, displayName, problem.id]);

  // Recorded solution walkthrough (Firestore). Null until loaded / if none.
  // `solutionLoaded` lets us tell "still loading" apart from "loaded, none found"
  // so we never flash a misleading simulated line over a real recorded one.
  const [solution, setSolution] = useState(null);
  const [solutionLoaded, setSolutionLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    setSolutionLoaded(false);
    loadSolution(problem.id).then((s) => {
      if (!alive) return;
      setSolution(s);
      setSolutionLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, [problem.id]);
  const finishRecording = useCallback(() => {
    loadSolution(problem.id).then((s) => {
      setSolution(s);
      setSolutionLoaded(true);
    });
    setPhase("solution");
  }, [problem.id]);

  // Mark this hand "played" (a tick on the Problem Hands list) once the member
  // finishes the hand and opens the solution. Saved to the account as well as this device.
  useEffect(() => {
    if (phase === "solution") recordPlayed();
  }, [phase, recordPlayed]);

  const isLocalhost =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1)/.test(window.location.hostname);
  const canView = isLocalhost || isAdmin || !!subscriptionActive;

  // Solution-video gating, mirroring the trainers (CountingTrumpsTrainer):
  // premium tier (or admin) can watch; basic members get an "Upgrade" CTA;
  // everyone else gets the "start a trial" CTA.
  const isMember = isLocalhost || isAdmin || !!subscriptionActive;
  const videoIsPremium = tier === "premium";
  const videoIsBasicMember = !isAdmin && isMember && tier === "basic";

  if (!authReady) {
    return (
      <div className="ph-page">
        <p>Checking access…</p>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="ph-page ph-gate">
        <h1>Problem Hands is for members</h1>
        {!uid ? (
          <Link to="/login?redirectTo=/just-play/problem-hands" className="ph-btn ph-btn--primary">
            Already a member? Log in
          </Link>
        ) : null}
        <Link
          to="/membership?redirectTo=/just-play/problem-hands"
          className={uid ? "ph-btn ph-btn--primary" : "ph-btn"}
        >
          View membership
        </Link>
      </div>
    );
  }

  const { contract, lead, deal } = problem;

  if (!contract || !lead) {
    const missing = [];
    if (!contract) missing.push("contract (declarer + level + strain)");
    if (!lead) missing.push("opening lead (seat + card)");
    return (
      <div className="ph-page">
        <div className="ph-backBar">
          <Link to="/just-play/problem-hands" className="ph-back">Problem Hands</Link>
        </div>
        <h1 className="ph-title">{withColoredSuits(problem.title)}</h1>
        <p style={{ color: "#888", fontSize: 17, marginBottom: 18 }}>
          The hands are set, but this problem still needs: {missing.join(" and ")}.
          Once those are added it becomes playable and you can record a solution.
        </p>
        <HandDiagram hands={deal.hands} suits={suitOrderForTrump(contract && contract.strain)} />
      </div>
    );
  }

  if (phase === "intro") {
    const rankDisplay = lead.card.rank === "T" ? "10" : lead.card.rank;
    return (
      <div className="ph-page">
        <div className="ph-backBar">
          <Link to="/just-play/problem-hands" className="ph-back">
            Problem Hands
          </Link>
        </div>
        <h1 className="ph-title">{withColoredSuits(problem.title)}</h1>

        <div className="ph-contractBar">
          <span>
            Contract:{" "}
            <strong>
              {contract.level}
              <span className={STRAIN_CLASS[contract.strain]}>
                {STRAIN_SYM[contract.strain]}
              </span>
            </strong>{" "}
            by South
          </span>
          <span className="ph-sep">·</span>
          <span>
            Lead:{" "}
            <strong>
              <span className={SUIT_CLASS[lead.card.suit]}>
                {rankDisplay}
                {SUIT_SYM[lead.card.suit]}
              </span>
            </strong>{" "}
            by West
          </span>
        </div>

        <HandDiagram hands={deal.hands} suits={suitOrderForTrump(contract && contract.strain)} />

        <div className="ph-intro">
          {problem.intro.map((para, i) => (
            <p key={i}>{withColoredSuits(para)}</p>
          ))}
          {problem.source && (
            <p className="ph-source">
              From the article: <a href={problem.source.href}>{withColoredSuits(problem.source.title)}</a>
            </p>
          )}
        </div>

        {problem.auction && problem.auction.length > 0 && (
          <AuctionGrid auction={problem.auction} dealer={deal.dealer} />
        )}
        {problem.auctionNote && <p className="ph-auctionNote">{withColoredSuits(problem.auctionNote)}</p>}

        <button
          className="ph-btn ph-btn--primary ph-startBtn"
          onClick={() => setPhase("playing")}
        >
          Start Playing
        </button>
      </div>
    );
  }

  if (phase === "playing") {
    // Paul, 9 Sep 2026: "this screen doesn't have the problem number listed anywhere" - the
    // play screen had no heading at all, so the title (with its number) now sits in the top bar.
    const idx = PROBLEM_HANDS.findIndex((h) => h.id === problem.id);
    return (
      <div className="ph-page ph-playPage">
        <div className="ph-backBar ph-backBar--play">
          <Link to="/just-play/problem-hands" className="ph-back">
            Problem Hands
          </Link>
          <h1 className="ph-title ph-title--play">
            {withColoredSuits(problem.title)}
            {idx >= 0 && <span className="ph-titleCount">Problem hand {idx + 1} of {PROBLEM_HANDS.length}</span>}
          </h1>
          <button className="ph-btn ph-showSolnBtn" onClick={handleExit}>
            Show me the solution
          </button>
        </div>
        <PlayTable
          embedded
          singleDeal
          defaultFast={false}
          problemSetup={{
            hands: problem.deal.hands,
            dealer: problem.deal.dealer,
            vul: problem.deal.vul,
            auction: problem.auction,
            contract: problem.contract,
            lead: problem.lead,
            forcedPlays: problem.forcedPlays,
            playRules: problem.playRules,
          }}
          exitLabel="View solution"
          onExit={handleExit}
          onResult={handleHandResult}
        />
        {/* Explanation + auction stay on the play screen, in a bordered card so
            they're readable and not jammed against the edges. */}
        <div className="ph-playInfo">
          {problem.intro && problem.intro.length > 0 && (
            <h3 className="ph-playInfoTitle">{withColoredSuits(problem.title)} Intro</h3>
          )}
          {problem.auction && problem.auction.length > 0 && (
            <AuctionGrid auction={problem.auction} dealer={deal.dealer} />
          )}
          {problem.auctionNote && <p className="ph-auctionNote">{withColoredSuits(problem.auctionNote)}</p>}
          {problem.intro && problem.intro.length > 0 && (
            <div className="ph-intro">
              {problem.intro.map((para, i) => (
                <p key={i}>{withColoredSuits(para)}</p>
              ))}
            </div>
          )}
          {(isLocalhost || isAdmin) && (
            <div style={{ textAlign: "center", marginTop: 14 }}>
              <button className="ph-btn" onClick={() => setPhase("record")}>
                ✎ Record / edit solution line (admin)
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === "record") {
    return (
      <div className="ph-page ph-playPage">
        <div className="ph-backBar">
          <Link to="/just-play/problem-hands" className="ph-back">
            Problem Hands
          </Link>
        </div>
        <h1 className="ph-title">{withColoredSuits(problem.title)} — Record solution (admin)</h1>
        <SolutionEditor problem={problem} onDone={finishRecording} />
      </div>
    );
  }

  return (
    <div className="ph-page">
      <div className="ph-backBar">
        <Link to="/just-play/problem-hands" className="ph-back">
          Problem Hands
        </Link>
      </div>
      <h1 className="ph-title">{withColoredSuits(problem.title)} — Solution</h1>
      {/* The data file's "(Solution to be added.)" placeholder is filler once the recorded
          walkthrough below exists, so it is no longer shown (25 Sep 2026). */}
      {problem.solution && !/^\(solution to be added\.?\)$/i.test(problem.solution.trim()) && (
        <div className="ph-solution">
          <p>{withColoredSuits(problem.solution)}</p>
        </div>
      )}
      <h3 className="ph-playInfoTitle">Watch the line</h3>
      {!solutionLoaded ? (
        <p className="sp-empty">Loading the solution…</p>
      ) : solution && solution.play && solution.play.length ? (
        // The recorded line + messages (the normal case).
        <SolutionPlayer
          problem={problem}
          play={solution.play}
          messages={solution.messages}
          videoUrl={solution.videoUrl}
          isPremium={videoIsPremium}
          isAdmin={isAdmin}
          isBasicMember={videoIsBasicMember}
        />
      ) : isLocalhost || isAdmin ? (
        // No line recorded yet — admins get a simulated placeholder to demo with,
        // clearly labelled so it's never mistaken for the real answer.
        <SolutionPlayer
          problem={problem}
          play={simulateLowestLine(deal.hands, contract, lead)}
          messages={{ 0: "(No line recorded yet — this is a placeholder. Record the real line + messages with the admin button below.)" }}
          videoUrl={solution && solution.videoUrl}
          isPremium={videoIsPremium}
          isAdmin={isAdmin}
          isBasicMember={videoIsBasicMember}
        />
      ) : (
        <p className="sp-empty">Solution coming soon.</p>
      )}
      {(isLocalhost || isAdmin) && (
        <div style={{ textAlign: "center", margin: "14px 0" }}>
          <button className="ph-btn" onClick={() => setPhase("record")}>
            ✎ Record / edit solution line (admin)
          </button>
        </div>
      )}
      <div className="ph-solutionActions">
        <button className="ph-btn" onClick={handlePlayAgain}>
          Play again
        </button>
        <Link to="/just-play/problem-hands" className="ph-btn ph-btn--primary">
          Back to list
        </Link>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  uid: state.auth?.uid,
  displayName: state.auth?.displayName || "",
  subscriptionActive: state.auth?.subscriptionActive === true,
  tier: state.auth?.tier ?? "basic",
  isAdmin: state.auth?.a === true,
  // Signed in but the members record has not arrived yet = still checking (not locked out).
  authReady: state.auth?.authReady === true && (!state.auth?.uid || state.auth?.subscriptionKnown === true || state.auth?.a === true),
});

export default connect(mapStateToProps, { savePracticeCompletion })(ProblemHandPlay);
